/**
 * The Query panel (ADR-038 Phase F; storyboard Flow X).
 *
 * The dockable surface where an operator asks an arbitrary question of the
 * store: pick a data type, compose a boolean predicate over its
 * attributes, and work the result set in the shared ``<DataTable>``.
 * Readable by every authenticated role (REQ-AUTH-075) — the disclosure
 * bound is the BFF's role-filtered catalog and re-validating compiler
 * (D9), not the visibility of this panel.
 *
 * # Composition, top to bottom
 *
 * 1. **Header** — data-type selector, result count, settings cog.
 * 2. **Builder** — the time window plus the persistent predicate chips
 *    (REQ-UI-116: the query stays visible after it runs).
 * 3. **Advisories** — the tier-boundary, lower-bound-total, and
 *    query-rejected notices. Each is a partially-answered question the
 *    operator must not have to infer (CLAUDE.md §8.1).
 * 4. **Results** — the shared table.
 * 5. **Pagination footer**.
 *
 * # Auto-run (D14 / REQ-UI-117)
 *
 * Editing a chip re-runs the query automatically after a 400 ms debounce,
 * and **only when the whole query validates**. An invalid chip shows its
 * error inline and issues no request. The debounce is on the *request*,
 * not on the keystroke: the tree updates immediately so typing stays
 * responsive, while the query key settles once the operator pauses.
 * TanStack Query cancels a superseded request via its ``signal``.
 *
 * # What resets what
 *
 * Changing the data type resets the predicate, the sort, and the page —
 * the attributes are different, so none of them carry over. Changing the
 * predicate, the window, or the sort resets the page to 1 (page 4 of the
 * previous result set means nothing in the new one) but preserves the
 * operator's chosen sort and columns.
 *
 * Cross-references:
 *   - REQ-UI-111 (dockable Query surface), REQ-UI-113..122.
 *   - REQ-QUERY-029..039; REQ-AUTH-075.
 *   - Feature spec §2.4, §2.5, §2.10; ADR-038 §Decision 5.
 */
import { useMemo, useState } from "react";
import { DataTable } from "@/data-table/data-table";
import { DataTableColumnMenu } from "@/data-table/data-table-column-menu";
import { DataTablePagination } from "@/data-table/data-table-pagination";
import {
  type SortState,
  moveColumnKey,
  toggleColumnVisibility,
  totalLabel,
} from "@/data-table/data-table-model";
import { useProducerNames } from "@/lib/use-producer-names";
import { queryColumns, queryRowKey, recordIdFor } from "@/query/query-columns";
import { useInspected, useSelectionActions } from "@/selection/selection-store";
import { QueryBuilder } from "@/query/query-builder";
import { QueryTimeRangeControl } from "@/query/query-time-range-control";
import {
  DEFAULT_TIME_RANGE,
  type GroupNode,
  type QueryRow,
  type QueryableType,
  type TimeRangeSelection,
  appendChild,
  attributeMap,
  defaultSortFor,
  deserializePredicate,
  emptyRoot,
  filterableAttributes,
  newComparison,
  retargetComparison,
  serializePredicate,
  setOperand,
  timeRangeError,
  toWireTimeRange,
  validatePredicate,
} from "@/query/query-model";
import { QueryExportAction } from "@/query/query-export";
import { type QueryHandoff, useQueryHandoffStore } from "@/query/query-handoff-store";
import { SavedQueryPicker } from "@/query/saved-query-picker";
import {
  type SavedQuery,
  predicateFromDocument,
  sortFromDocument,
  timeRangeFromDocument,
  useQueryChrome,
} from "@/query/use-saved-queries";
import {
  type RecordsRequest,
  queryErrorDetail,
  recordsRequestBody,
  useQueryCatalog,
  useQueryRecords,
} from "@/query/use-query-data";
import { useDebounced } from "@/query/use-debounced";
import { formatQueryTime } from "@/query/query-columns";

/** Auto-run debounce (D14). Long enough to absorb typing, short enough to feel live. */
const AUTO_RUN_DEBOUNCE_MS = 400;

/** Render the Query panel. */
export function QueryPanel(): React.ReactElement {
  const catalog = useQueryCatalog();
  const producerNames = useProducerNames();
  const queryRequest = useQueryHandoffStore((state) => state.request);
  const { inspect } = useSelectionActions();
  const inspected = useInspected();

  const [typeId, setTypeId] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<TimeRangeSelection>(DEFAULT_TIME_RANGE);
  const [predicate, setPredicate] = useState<GroupNode>(emptyRoot);
  const [sort, setSort] = useState<SortState>([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [openedQuery, setOpenedQuery] = useState<SavedQuery | null>(null);
  // The type the query state was last reset for. Opening a saved query
  // sets this to the query's type in the SAME update as the type switch, so
  // the reset below does not clobber the sort and predicate the saved query
  // just loaded. State rather than a ref because the reset is decided during
  // render: a ref written during a render React then discards would keep the
  // write while losing the state it was claiming, and the type would never be
  // seeded again.
  const [resetForType, setResetForType] = useState<string | null>(null);
  const [appliedRequest, setAppliedRequest] = useState<QueryHandoff | null>(null);

  const types = catalog.data?.types ?? [];
  const selectedType: QueryableType | null =
    types.find((type) => type.type_id === typeId) ?? types[0] ?? null;

  // Apply a cross-pane request — "show this platform's records of this kind"
  // (REQ-UI-189) — before the type-change reset below, claiming that reset for
  // the requested type exactly as :func:`handleOpenSaved` does so it cannot
  // overwrite the predicate being seeded.
  //
  // DURING RENDER, for the same reason the reset below is: React re-runs the
  // component on a set-state during render and discards the in-progress
  // output, so the opening request is composed from the seeded state rather
  // than from one render of an unseeded panel. An effect here would also be
  // the `react-hooks/set-state-in-effect` cascade whose waiver list
  // `provider-editor.tsx` records as an inventory to shrink.
  //
  // Gated on a loaded catalog (`types.length > 0`): the types arrive by fetch,
  // so the first render has nothing to resolve the request against, and
  // marking it applied there would silently lose the click of an operator
  // whose Query panel was cold. It IS marked applied when the catalog loaded
  // but carries no such type — a request this role will never be able to see
  // must not be retried on every render.
  let seedingFromRequest = false;
  if (queryRequest !== null && queryRequest !== appliedRequest && types.length > 0) {
    setAppliedRequest(queryRequest);
    const target = types.find((candidate) => candidate.type_id === queryRequest.typeId);
    const attributes = target === undefined ? undefined : attributeMap(target);
    // EVERY predicate must resolve, or the request is dropped whole.
    //
    // The all-or-nothing rule is the honest one: seeding a subset would show a
    // BROADER result set than the operator asked for while looking like the
    // answer to their click — all quarantines when they asked for one reason,
    // every platform's records when they asked for one platform's. An attribute
    // can be absent legitimately (a role-filtered projection removes what the
    // caller may not see, REQ-AUTH-076), so this is a reachable path, not a
    // defensive branch.
    const resolved =
      attributes === undefined
        ? undefined
        : queryRequest.predicates.map((seed) => ({
            attribute: attributes.get(seed.attribute),
            value: seed.value,
          }));
    if (
      target !== undefined &&
      resolved !== undefined &&
      resolved.every((seed) => seed.attribute !== undefined)
    ) {
      const base = emptyRoot();
      const seeded = resolved.reduce(
        (tree, seed) =>
          appendChild(
            tree,
            tree.id,
            // `attribute` is proven defined by the `every` guard above; the
            // non-null assertion is the narrowing TypeScript cannot carry
            // across the closure boundary.
            setOperand(retargetComparison(newComparison(), seed.attribute!), 0, seed.value),
          ),
        base,
      );
      setResetForType(queryRequest.typeId);
      setTypeId(queryRequest.typeId);
      setPredicate(seeded);
      setSort(defaultSortFor(target));
      setPageIndex(0);
      setOpenedQuery(null);
      seedingFromRequest = true;
    }
  }

  // Reset the *query* whenever the selected type changes — the attributes
  // are different, so neither the predicate nor the sort carries over. This
  // also seeds the default sort on first load. The table CHROME is not
  // reset here: it is per-type and persisted, so switching back to a type
  // restores the columns the operator arranged (D13 / REQ-UI-128).
  //
  // This runs DURING RENDER rather than in an effect, because the opening
  // request is composed from this state on the very first render that has a
  // type. An effect runs after that render has already been committed, which
  // left a window in which the panel had composed a sort-less query — masked
  // until now only by the 400 ms debounce swallowing it. React re-runs the
  // component immediately on a set-state during render and throws the
  // in-progress output away, so nothing downstream ever observes the
  // unseeded state. (React's "adjusting state when a prop changes" pattern.)
  //
  // Skipped on a pass that seeded from a cross-pane request. Both blocks run in
  // the SAME pass, and `selectedType` / `resetForType` were read before the
  // seed's setters — so this condition still sees the outgoing type and would
  // reset the predicate that was just seeded. (`handleOpenSaved` never meets
  // this: it is an event handler, so its next render reads consistent values.)
  if (!seedingFromRequest && selectedType !== null && resetForType !== selectedType.type_id) {
    setResetForType(selectedType.type_id);
    setSort(defaultSortFor(selectedType));
    setPredicate(emptyRoot());
    setPageIndex(0);
    setOpenedQuery(null);
  }

  const attributes = useMemo(
    () => (selectedType === null ? new Map() : attributeMap(selectedType)),
    [selectedType],
  );
  const filterable = useMemo(
    () => (selectedType === null ? [] : filterableAttributes(selectedType)),
    [selectedType],
  );

  const columns = useMemo(
    () => (selectedType === null ? [] : queryColumns(selectedType, producerNames)),
    [selectedType, producerNames],
  );
  // Table chrome lives in the ADR-033 Profile document, per data type, so
  // Profile apply restores it along with the theme and the workspace
  // (D13 / REQ-UI-128).
  const chrome = useQueryChrome(selectedType, columns);
  const pageSize = chrome.pageSize ?? 50;

  const bounds = {
    maxComparisons: catalog.data?.max_comparisons ?? 32,
    maxNestingDepth: catalog.data?.max_nesting_depth ?? 4,
  };
  const validation = validatePredicate(predicate, attributes, bounds);
  const windowError = timeRangeError(timeRange);
  const runnable = selectedType !== null && validation.valid && windowError === null;

  // No request object at all while the query is unrunnable — an invalid
  // chip or a half-typed absolute window must not reach the wire, and
  // building a body from one would put an unparseable instant on the
  // render path (D14 / REQ-UI-117).
  const request: RecordsRequest | null = runnable
    ? {
        type: selectedType.type_id,
        timeRange,
        predicate: serializePredicate(predicate, attributes),
        sort,
        pageIndex,
        pageSize,
      }
    : null;
  // Debounce the REQUEST, not the keystroke: the chips update immediately
  // so typing stays responsive, while the query key settles on a pause.
  const debouncedRequest = useDebounced(request, AUTO_RUN_DEBOUNCE_MS);
  const records = useQueryRecords(debouncedRequest, { enabled: runnable });

  const rows = (records.data?.items ?? []) as QueryRow[];
  const serverError = queryErrorDetail(records.error);

  const handleTypeChange = (nextTypeId: string): void => {
    setTypeId(nextTypeId);
  };

  // Any change to what is being asked resets the page — page 4 of the
  // previous result set has no meaning in the new one.
  const withPageReset =
    <T,>(setter: (value: T) => void) =>
    (value: T): void => {
      setter(value);
      setPageIndex(0);
    };

  // The query as it would be saved. Built from the live composition so a
  // save always stores what the operator is looking at.
  const currentDocument = {
    time_range: toWireTimeRange(timeRange) as unknown as Record<string, unknown>,
    predicate: (runnable ? serializePredicate(predicate, attributes) : null) as unknown as Record<
      string,
      unknown
    > | null,
    sort: sort.map((rule) => ({ attribute: rule.key, direction: rule.direction })),
    columns: [...chrome.visibleColumns],
  };

  /**
   * Load a saved query into the panel.
   *
   * The predicate is rebuilt into the *editable* tree so the operator can
   * refine it, and the type is switched first so the chips validate against
   * the right attribute set. A comparison naming an attribute the catalog
   * no longer offers survives as a chip marked "Unknown attribute" rather
   * than being silently dropped — the operator should see what their saved
   * query actually said.
   */
  const handleOpenSaved = (query: SavedQuery): void => {
    const target = types.find((candidate) => candidate.type_id === query.type_id);
    // Claim the reset for this type alongside the switch, so the type-change
    // reset does not overwrite the state the saved query is loading. Both
    // updates land in the same batch, so the reset never sees a mismatch.
    setResetForType(query.type_id);
    setTypeId(query.type_id);
    setOpenedQuery(query);
    setTimeRange(timeRangeFromDocument(query.document));
    setSort(sortFromDocument(query.document));
    setPredicate(
      deserializePredicate(
        predicateFromDocument(query.document),
        target === undefined ? new Map() : attributeMap(target),
      ),
    );
    setPageIndex(0);
  };

  if (catalog.isLoading) {
    return (
      <PanelShell testId="query-panel-loading" role="status" label="Loading query catalog">
        <p className="text-body-sm text-text-secondary">Loading the data catalog…</p>
      </PanelShell>
    );
  }

  if (catalog.isError) {
    return (
      <PanelShell testId="query-panel-error" role="alert" label="Query catalog unavailable">
        <p className="text-body text-destructive">Could not load the data catalog</p>
        <p className="max-w-md text-center text-body-sm text-text-secondary">
          {catalog.error.message}
        </p>
        <button
          type="button"
          onClick={() => void catalog.refetch()}
          className="rounded border border-border bg-bg-surface px-3 py-1 text-body-sm text-text-primary hover:bg-bg-elevated"
          data-testid="query-panel-retry"
        >
          Retry
        </button>
      </PanelShell>
    );
  }

  if (selectedType === null) {
    return (
      <PanelShell testId="query-panel-empty-catalog" role="region" label="No queryable data">
        <p className="text-body text-text-primary">No data types are available to query</p>
        <p className="text-body-sm text-text-muted">
          Your role has access to no queryable data types in this deployment.
        </p>
      </PanelShell>
    );
  }

  // Below here ``selectedType`` is non-null. Highlight the row currently
  // in the inspected slot — but only when the inspected record belongs to
  // the type on screen, so switching type does not leave a stale highlight
  // on a same-id row of a different type.
  const inspectedRecordId =
    inspected?.kind === "query-record" && inspected.recordType === selectedType.type_id
      ? inspected.id
      : null;
  const selectedRowIndex =
    inspectedRecordId === null
      ? -1
      : rows.findIndex((row) => recordIdFor(selectedType, row) === inspectedRecordId);
  const selectedRowKey =
    selectedRowIndex === -1
      ? null
      : queryRowKey(selectedType, rows[selectedRowIndex] as QueryRow, selectedRowIndex);

  return (
    <div
      data-testid="query-panel"
      role="region"
      aria-label="Query"
      className="flex h-full w-full flex-col bg-bg-base"
    >
      <header className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
        <div className="flex items-center gap-2">
          <h2 className="text-body font-medium text-text-primary">Query</h2>
          <label className="flex items-center gap-1">
            <span className="sr-only">Data type</span>
            <select
              value={selectedType.type_id}
              onChange={(event) => handleTypeChange(event.target.value)}
              aria-label="Data type"
              data-testid="query-type-select"
              className="rounded border border-border bg-bg-base px-1.5 py-1 text-body-sm text-text-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-accent"
            >
              {types.map((type) => (
                <option key={type.type_id} value={type.type_id}>
                  {type.label}
                </option>
              ))}
            </select>
          </label>
          {records.data !== undefined && (
            <span className="text-body-sm text-text-muted" data-testid="query-result-count">
              {totalLabel(records.data.total, records.data.total_is_lower_bound)} records
              <span className="ml-2 text-meta">{records.data.duration_ms} ms</span>
            </span>
          )}
        </div>
        <DataTableColumnMenu
          columns={columns}
          columnOrder={chrome.columnOrder}
          visibleColumns={chrome.visibleColumns}
          onToggleColumn={(key) =>
            chrome.setVisibleColumns(toggleColumnVisibility(chrome.visibleColumns, key))
          }
          onMoveColumn={(key, direction) => {
            const order = chrome.columnOrder;
            const index = order.indexOf(key);
            const target = order[direction === "left" ? index - 1 : index + 1];
            if (target !== undefined) {
              chrome.setColumnOrder(moveColumnKey(order, key, target));
            }
          }}
          extraAction={
            <QueryExportAction
              body={request === null ? null : recordsRequestBody(request)}
              columns={[...chrome.visibleColumns]}
              typeId={selectedType.type_id}
            />
          }
          testId="query-settings"
        />
      </header>

      <div className="flex flex-col gap-2 border-b border-border px-4 py-2">
        <div className="flex flex-wrap items-center gap-2">
          <QueryTimeRangeControl
            value={timeRange}
            onChange={withPageReset(setTimeRange)}
            error={windowError}
          />
          <SavedQueryPicker
            currentDocument={currentDocument}
            currentTypeId={selectedType.type_id}
            openedQuery={openedQuery}
            onOpen={handleOpenSaved}
            onSaved={setOpenedQuery}
            onDeleted={() => setOpenedQuery(null)}
          />
        </div>
        <QueryBuilder
          root={predicate}
          attributes={filterable}
          errors={validation.errors}
          onChange={withPageReset(setPredicate)}
          // Same inventory the results table joins on, so the name an operator
          // picks is the name the Source column reads back (REQ-UI-188).
          producerOptions={producerNames.options}
        />
        {validation.formError !== null && (
          <p role="alert" className="text-body-sm text-destructive" data-testid="query-form-error">
            {validation.formError}
          </p>
        )}
      </div>

      <QueryAdvisories
        truncatedRange={records.data?.truncated_range ?? null}
        totalIsLowerBound={records.data?.total_is_lower_bound ?? false}
        total={records.data?.total ?? 0}
        serverMessage={serverError?.message ?? null}
      />

      <div className="min-h-0 flex-1">
        {records.isError && serverError === null ? (
          <PanelShell testId="query-results-error" role="alert" label="Query failed">
            <p className="text-body text-destructive">The query could not be run</p>
            <p className="max-w-md text-center text-body-sm text-text-secondary">
              {records.error.message}
            </p>
          </PanelShell>
        ) : (
          <DataTable<QueryRow>
            columns={columns}
            rows={rows}
            rowKey={(row) => queryRowKey(selectedType, row, rows.indexOf(row))}
            selectedRowKey={selectedRowKey}
            onRowActivate={(row) => {
              const id = recordIdFor(selectedType, row);
              if (id !== null) {
                // The inspected slot ONLY — never mapSelected. A query
                // selection must not move the map camera (ADR-024 §5
                // asymmetric bus / ADR-038 §Decision 5).
                inspect({ kind: "query-record", id, recordType: selectedType.type_id });
              }
            }}
            caption={`${selectedType.label} query results`}
            sort={sort}
            onSortChange={withPageReset(setSort)}
            columnOrder={chrome.columnOrder}
            onColumnOrderChange={chrome.setColumnOrder}
            visibleColumns={chrome.visibleColumns}
            columnSizing={chrome.columnSizing}
            onColumnSizingChange={chrome.setColumnSizing}
            emptyMessage={
              records.isFetching ? "Running…" : "No records match this query in the selected window"
            }
            testId="query-results"
          />
        )}
      </div>

      <DataTablePagination
        pageIndex={pageIndex}
        pageSize={pageSize}
        itemsOnPage={rows.length}
        total={records.data?.total ?? 0}
        totalIsLowerBound={records.data?.total_is_lower_bound ?? false}
        pageSizeOptions={catalog.data?.page_size_options ?? [25, 50, 100, 250]}
        onPageIndexChange={setPageIndex}
        onPageSizeChange={(size) => {
          chrome.setPageSize(size);
          setPageIndex(0);
        }}
        testId="query-pagination"
      />
    </div>
  );
}

interface QueryAdvisoriesProps {
  truncatedRange: string | null;
  totalIsLowerBound: boolean;
  total: number;
  serverMessage: string | null;
}

/**
 * The advisories strip: a partially-answered question must say so.
 *
 * - **Tier boundary** — the requested window reached past the hot/warm
 *   tier, so the result covers only part of what was asked (D23 / §2.5).
 *   Silence here would present an incomplete answer as complete.
 * - **Bounded total** — the count hit its ceiling, so the total is a floor
 *   (D6). Rendered as "100,000+" everywhere, never as an exact count.
 * - **Rejected query** — the BFF's own explanation, shown verbatim rather
 *   than replaced with a generic failure.
 */
function QueryAdvisories({
  truncatedRange,
  totalIsLowerBound,
  total,
  serverMessage,
}: QueryAdvisoriesProps): React.ReactElement | null {
  if (truncatedRange === null && !totalIsLowerBound && serverMessage === null) {
    return null;
  }
  return (
    <div className="flex flex-col gap-1 border-b border-border bg-bg-surface px-4 py-1.5">
      {serverMessage !== null && (
        <p role="alert" className="text-body-sm text-destructive" data-testid="query-server-error">
          {serverMessage}
        </p>
      )}
      {truncatedRange !== null && (
        <p role="status" className="text-body-sm text-warning" data-testid="query-tier-advisory">
          Results cover {formatQueryTime(truncatedRange)} UTC onward; older records are in the
          archive tier and were not searched.
        </p>
      )}
      {totalIsLowerBound && (
        <p role="status" className="text-body-sm text-warning" data-testid="query-count-advisory">
          More than {totalLabel(total, false)} records match. Narrow the window or add a condition
          to see an exact count.
        </p>
      )}
    </div>
  );
}

/** Centred full-panel state (loading / error / empty-catalog). */
function PanelShell({
  testId,
  role,
  label,
  children,
}: {
  testId: string;
  role: "status" | "alert" | "region";
  label: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div
      data-testid={testId}
      role={role}
      aria-label={label}
      className="flex h-full w-full flex-col items-center justify-center gap-2 bg-bg-base"
    >
      {children}
    </div>
  );
}
